/* Career Clarity Subject Layer — reusable renderer for all 50 result profiles.
   Reuses the Future-Ready Pathway overlay family (dark navy shell, parchment +
   teal accents, numbered pills, one-item-at-a-time focus). Content is rendered
   verbatim from the active package's result profile (package + exact tag). */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, BookOpen, ChevronLeft } from 'lucide-react';

const PARCHMENT = '#F5E9D4';
const DEEP = '#0B1F2E';
const PANEL = '#1A3548';
const TEAL = '#2DD4BF';
const SERIF = '"Fraunces", "Cormorant Garamond", "Georgia", serif';
const BAND_LABEL = { start: 'Start', mid: 'Mid', complex: 'Complex' };

/* Resolve the subject layer from the ACTIVE package + EXACT result tag.
   Never matches by title or position; never substitutes another profile. */
export function resolveSubjectLayer(pkg, tag) {
  const profile = (pkg?.result_profiles || []).find((p) => p.tag === tag);
  const layer = profile?.subject_layer;
  if (!layer?.subjects?.length) {
    return { layer: null, error: `No subject layer found for package "${pkg?.package_name || 'unknown'}" and result tag "${tag}".` };
  }
  return { layer, error: null };
}

const Pill = ({ children, onClick, testId, subtle = false, disabled = false, ariaLabel }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
    className="rounded-full px-6 font-semibold uppercase tracking-[0.14em] transition-transform hover:scale-[1.02] disabled:opacity-30 disabled:hover:scale-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-300"
    style={{
      background: subtle ? 'transparent' : PARCHMENT,
      color: subtle ? PARCHMENT : DEEP,
      border: subtle ? `1px solid ${PARCHMENT}66` : 'none',
      minHeight: 46, fontSize: 12,
    }}
    data-testid={testId}
  >{children}</button>
);

const BandChip = ({ band }) => (
  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold shrink-0"
    style={{ border: `1px solid ${TEAL}66`, color: TEAL }}>
    {BAND_LABEL[band] || band}
  </span>
);

/* Numbered 1–8 progress: visited = parchment outline, active = teal, rest inactive.
   Numbers + aria labels ensure colour is never the only indicator. */
const NumberRow = ({ subjects, active, visited, onPick }) => (
  <div className="flex items-center gap-2 flex-wrap" role="group" aria-label="Subject progress, 8 subjects">
    {subjects.map((s, i) => {
      const isActive = i === active;
      const isVisited = visited.has(i) && !isActive;
      return (
        <button key={i} type="button" onClick={() => onPick(i)}
          aria-label={`Subject ${i + 1} of ${subjects.length}: ${s.name}${isActive ? ' (current)' : isVisited ? ' (visited)' : ''}`}
          aria-current={isActive ? 'step' : undefined}
          className="rounded-full font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-300"
          style={{
            width: 44, height: 44, fontSize: 13,
            background: isActive ? TEAL : isVisited ? `${PARCHMENT}22` : `${PARCHMENT}0D`,
            color: isActive ? DEEP : isVisited ? PARCHMENT : `${PARCHMENT}88`,
            border: `1px solid ${isActive ? TEAL : isVisited ? `${PARCHMENT}66` : `${PARCHMENT}33`}`,
          }}
          data-testid={`cc-sl-num-${i}`}
        >{i + 1}</button>
      );
    })}
  </div>
);

const SubjectLayerOverlay = ({ layer, error, onClose }) => {
  const [sel, setSel] = useState(null); // null = overview, 0..7 = subject
  const [visited, setVisited] = useState(() => new Set());
  const shellRef = useRef(null);
  const subjects = (layer?.subjects || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));

  const openSubject = (i) => {
    setVisited((v) => new Set(v).add(i));
    setSel(i);
  };

  /* Scroll lock + Escape + focus trap (same family as the pathway shell). */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const node = shellRef.current;
    node?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
      if (e.key !== 'Tab' || !node) return;
      const focusables = node.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey, true);
    };
  }, [onClose]);

  /* Scroll the internal pane to top when switching views. */
  const paneRef = useRef(null);
  useEffect(() => { paneRef.current?.scrollTo?.(0, 0); }, [sel]);

  const m = sel !== null ? subjects[sel] : null;
  const bands = ['start', 'mid', 'complex'];

  /* Portal to <body>: escapes the sticky-nav stacking context so the shell
     covers the whole page (same full-screen behaviour as the pathway). */
  return createPortal(
    <div
      ref={shellRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label="Subjects for your career clarity result"
      className="fixed inset-0 z-[95] flex flex-col outline-none"
      style={{ background: DEEP, fontFamily: '"Inter", system-ui, sans-serif' }}
      data-testid="cc-sl-overlay"
    >
      <div className="flex items-center justify-between px-4 pt-4 pb-2 shrink-0">
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] pl-1" style={{ color: `${PARCHMENT}77` }}>
          Subjects
        </span>
        <button type="button" onClick={onClose}
          className="inline-flex items-center gap-1 rounded-full px-4 text-xs uppercase tracking-widest focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-300"
          style={{ color: `${PARCHMENT}88`, minHeight: 44 }}
          data-testid="cc-sl-exit">
          Exit <X size={14} />
        </button>
      </div>

      <div ref={paneRef} className="flex-1 overflow-y-auto overscroll-contain px-5"
        style={{ paddingBottom: 'calc(4rem + env(safe-area-inset-bottom, 0px))' }}>
        <div className="w-full max-w-2xl mx-auto">

          {error && (
            <div className="text-center py-16" data-testid="cc-sl-error">
              <p style={{ color: PARCHMENT, fontSize: 15 }}>{error}</p>
              <div className="mt-6"><Pill onClick={onClose} testId="cc-sl-error-close">Close</Pill></div>
            </div>
          )}

          {!error && sel === null && (
            <div data-testid="cc-sl-overview">
              <h1 className="italic mb-4 mt-2"
                style={{ color: PARCHMENT, fontFamily: SERIF, fontSize: 'clamp(22px,4vw,32px)', lineHeight: 1.2 }}
                data-testid="cc-sl-title">
                {layer.title}
              </h1>
              <p className="leading-relaxed break-words [overflow-wrap:anywhere] mb-6"
                style={{ color: `${PARCHMENT}DD`, fontSize: 14 }} data-testid="cc-sl-intro">
                {layer.intro}
              </p>
              <div className="space-y-5">
                {bands.map((band) => {
                  const group = subjects.map((s, i) => ({ s, i })).filter(({ s }) => s.band === band);
                  if (!group.length) return null;
                  return (
                    <div key={band}>
                      <p className="text-[10px] font-bold uppercase tracking-[0.3em] mb-2" style={{ color: `${TEAL}CC` }}>
                        {BAND_LABEL[band]}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {group.map(({ s, i }) => (
                          <button key={i} type="button" onClick={() => openSubject(i)}
                            aria-label={`Open subject ${i + 1} of ${subjects.length}: ${s.name} (${BAND_LABEL[s.band]})`}
                            className="rounded-full px-3.5 py-2 text-[12px] font-semibold transition-colors break-words max-w-full text-left inline-flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-300 hover:scale-[1.02]"
                            style={{
                              minHeight: 44,
                              background: `${PARCHMENT}0D`,
                              color: `${PARCHMENT}CC`,
                              border: `1px solid ${PARCHMENT}33`,
                            }}
                            data-testid={`cc-sl-pill-${i}`}>
                            <span>{i + 1}. {s.name}</span>
                            <BandChip band={s.band} />
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!error && m && (
            <div data-testid="cc-sl-subject-view">
              <button type="button" onClick={() => setSel(null)}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-widest mt-2 mb-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-300"
                style={{ border: `1px solid ${PARCHMENT}44`, color: `${PARCHMENT}AA`, minHeight: 44 }}
                data-testid="cc-sl-back">
                <ChevronLeft size={13} /> Back to subjects
              </button>

              <div className="mb-5">
                <NumberRow subjects={subjects} active={sel} visited={visited} onPick={openSubject} />
              </div>

              <p className="text-[11px] font-bold uppercase tracking-[0.2em] mb-2" style={{ color: `${PARCHMENT}77` }}
                aria-live="polite" data-testid="cc-sl-progress">
                Subject {sel + 1} / {subjects.length}
              </p>

              <div className="rounded-2xl px-4 py-4 md:px-5 md:py-5 space-y-3"
                style={{ background: PANEL, border: `1px solid ${TEAL}55` }}
                data-testid="cc-sl-card">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <p className="font-bold leading-snug break-words min-w-0" style={{ color: PARCHMENT, fontSize: 16.5 }}>
                    {m.order}. {m.name}
                  </p>
                  <BandChip band={m.band} />
                </div>
                <p className="leading-relaxed break-words [overflow-wrap:anywhere]"
                  style={{ color: `${PARCHMENT}DD`, fontSize: 14, lineHeight: 1.75 }}
                  data-testid="cc-sl-what-it-is">
                  {m.what_it_is}
                </p>
              </div>

              <div className="flex items-center justify-between gap-3 flex-wrap mt-6">
                <Pill subtle onClick={() => openSubject(Math.max(0, sel - 1))} disabled={sel <= 0} testId="cc-sl-prev">Previous</Pill>
                <span className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: `${PARCHMENT}77` }}>
                  {sel + 1} / {subjects.length}
                </span>
                <Pill onClick={() => openSubject(Math.min(subjects.length - 1, sel + 1))} disabled={sel >= subjects.length - 1} testId="cc-sl-next">Next</Pill>
              </div>
            </div>
          )}

          {!error && (
            <div className="text-center mt-10">
              <Pill onClick={onClose} testId="cc-sl-done">Back to my result</Pill>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

/* Teal "Subjects" gateway button — first chip in the result-navigation row. */
export const ClaritySubjectsButton = ({ pkg, topTag }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const { layer, error } = resolveSubjectLayer(pkg, topTag);

  const close = () => {
    setOpen(false);
    requestAnimationFrame(() => btnRef.current?.focus());
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold text-white bg-teal-600 border border-teal-600 hover:bg-teal-700 rounded-full px-3.5 py-1.5 transition-colors active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 focus-visible:ring-offset-1"
        data-testid="cc-subjects-btn"
      >
        <BookOpen size={12} /> Subjects
      </button>
      {open && <SubjectLayerOverlay layer={layer} error={error} onClose={close} />}
    </>
  );
};

/* Dark gateway card — same anatomy as the Future-Ready Pathway layer entry
   (dark rounded-3xl card, small-caps eyebrow, italic serif line, parchment
   "Open" pill). Placed in the report below "Other branches that suit you too". */
export const ClaritySubjectsGateway = ({ pkg, topTag }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const { layer, error } = resolveSubjectLayer(pkg, topTag);

  const close = () => {
    setOpen(false);
    requestAnimationFrame(() => btnRef.current?.focus());
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="w-full rounded-3xl px-6 py-6 text-left transition-transform hover:scale-[1.01] focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400"
        style={{ background: DEEP, minHeight: 44 }}
        data-testid="cc-subjects-gateway"
      >
        <span className="block text-[10px] font-bold uppercase tracking-[0.3em] mb-1.5" style={{ color: `${PARCHMENT}77` }}>
          Subjects
        </span>
        <span className="flex items-center justify-between gap-3">
          <span className="italic min-w-0 break-words" style={{ color: PARCHMENT, fontFamily: SERIF, fontSize: 'clamp(18px,2.4vw,24px)' }}>
            {layer?.title || 'Subjects for your result'}
          </span>
          <span className="rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-widest shrink-0"
            style={{ background: PARCHMENT, color: DEEP }}>
            Open
          </span>
        </span>
      </button>
      {open && <SubjectLayerOverlay layer={layer} error={error} onClose={close} />}
    </>
  );
};

export default ClaritySubjectsButton;
